// client/src/components/app/context/ActivityTrackingContext.js
// Heartbeat-based activity tracking for user engagement monitoring
// Sends heartbeat every 5 seconds while app is in foreground
// Pauses when app goes to background, resumes when it comes back

import React, { createContext, useContext, useEffect, useRef, useCallback, useState } from 'react';
import { api } from '../../../utils/apiClient';
import { HEARTBEAT_INTERVAL_MS, VISIBILITY_DEBOUNCE_MS, IDLE_CHECK_INTERVAL_MS } from '../../../constants/time';

// ==================== CONSTANTS ====================

const IDLE_TIMEOUT_MS = parseInt(process.env.REACT_APP_IDLE_TIMEOUT_MS) || 15 * 60 * 1000; // 15 minutes
const IDLE_WARNING_MS = IDLE_TIMEOUT_MS * 0.8; // Show warning at 80% of timeout

// ==================== CONTEXT ====================

const ActivityTrackingContext = createContext({
  isTracking: false,
  sessionId: null,
  activeTimeMs: 0,
  idleWarningVisible: false,
  dismissIdleWarning: () => {},
  idleSecondsRemaining: 0
});

// ==================== PROVIDER ====================

/**
 * ActivityTrackingProvider
 * 
 * Wraps the app to provide automatic heartbeat tracking.
 * - Sends heartbeat every 5 seconds while page is visible
 * - Pauses heartbeats when page goes to background
 * - Resumes when page becomes visible again
 * - Ends session on logout or when app is closed
 * 
 * Usage:
 * Wrap your authenticated app content with this provider.
 * The provider automatically handles all tracking logic.
 */
export const ActivityTrackingProvider = ({ children, isAuthenticated = false, isDisplayAccount = false }) => {
  // State
  const [isTracking, setIsTracking] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [activeTimeMs, setActiveTimeMs] = useState(0);
  const [idleWarningVisible, setIdleWarningVisible] = useState(false);
  const [idleSecondsRemaining, setIdleSecondsRemaining] = useState(0);

  // Refs for intervals and state
  const heartbeatIntervalRef = useRef(null);
  const idleCheckIntervalRef = useRef(null);
  const isVisibleRef = useRef(true);
  const lastHeartbeatRef = useRef(Date.now());
  const lastUserInteractionRef = useRef(Date.now());
  const isAuthenticatedRef = useRef(isAuthenticated);
  
  // Update ref when isAuthenticated changes
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);
  
  /**
   * Send a single heartbeat to the server
   */
  const sendHeartbeat = useCallback(async () => {
    // Don't send if not authenticated or page not visible
    if (!isAuthenticatedRef.current || !isVisibleRef.current) {
      return;
    }
    
    try {
      const response = await api.activity.heartbeat();
      
      if (response?.data) {
        const { sessionId: newSessionId, activeTimeMs: newActiveTime, isNew } = response.data;
        
        if (newSessionId) {
          setSessionId(newSessionId);
        }
        if (typeof newActiveTime === 'number') {
          setActiveTimeMs(newActiveTime);
        }
        
        // Log new sessions for debugging
        if (isNew) {
          console.debug('[ActivityTracking] New session started:', newSessionId);
        }
      }
      
      lastHeartbeatRef.current = Date.now();
      setIsTracking(true);
      
    } catch (error) {
      // Silently fail - heartbeats are not critical
      // Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.debug('[ActivityTracking] Heartbeat failed:', error.message);
      }
    }
  }, []);
  
  /**
   * Start the heartbeat interval
   */
  const startHeartbeats = useCallback(() => {
    // Clear any existing interval
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    
    // Send initial heartbeat
    sendHeartbeat();
    
    // Start interval
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
    
    console.debug('[ActivityTracking] Heartbeats started');
  }, [sendHeartbeat]);
  
  /**
   * Stop the heartbeat interval
   */
  const stopHeartbeats = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    
    setIsTracking(false);
    console.debug('[ActivityTracking] Heartbeats stopped');
  }, []);
  
  /**
   * End the current session
   * Called on logout
   */
  const endSession = useCallback(async () => {
    stopHeartbeats();
    
    if (isAuthenticatedRef.current) {
      try {
        await api.activity.endSession();
        console.debug('[ActivityTracking] Session ended');
      } catch (error) {
        // Silently fail
        console.debug('[ActivityTracking] Failed to end session:', error.message);
      }
    }
    
    setSessionId(null);
    setActiveTimeMs(0);
  }, [stopHeartbeats]);
  
  /**
   * Handle page visibility changes
   * Pause heartbeats when page goes to background, resume when it comes back
   */
  useEffect(() => {
    let visibilityTimeout = null;
    
    const handleVisibilityChange = () => {
      // Debounce to avoid rapid toggling
      if (visibilityTimeout) {
        clearTimeout(visibilityTimeout);
      }
      
      visibilityTimeout = setTimeout(() => {
        const isVisible = document.visibilityState === 'visible';
        isVisibleRef.current = isVisible;
        
        if (!isAuthenticatedRef.current) return;
        
        if (isVisible) {
          // Page became visible - resume heartbeats
          console.debug('[ActivityTracking] Page visible - resuming heartbeats');
          startHeartbeats();
        } else {
          // Page went to background - pause heartbeats
          console.debug('[ActivityTracking] Page hidden - pausing heartbeats');
          stopHeartbeats();
        }
      }, VISIBILITY_DEBOUNCE_MS);
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (visibilityTimeout) {
        clearTimeout(visibilityTimeout);
      }
    };
  }, [startHeartbeats, stopHeartbeats]);
  
  /**
   * Handle authentication state changes
   * Start heartbeats when authenticated, stop when logged out
   */
  useEffect(() => {
    if (isAuthenticated) {
      // User logged in - start heartbeats
      console.debug('[ActivityTracking] User authenticated - starting tracking');
      startHeartbeats();
    } else {
      // User logged out - end session and stop heartbeats
      console.debug('[ActivityTracking] User logged out - stopping tracking');
      endSession();
    }
    
    // Cleanup on unmount
    return () => {
      stopHeartbeats();
    };
  }, [isAuthenticated, startHeartbeats, stopHeartbeats, endSession]);
  
  /**
   * Dismiss idle warning and reset interaction timer
   */
  const dismissIdleWarning = useCallback(() => {
    lastUserInteractionRef.current = Date.now();
    setIdleWarningVisible(false);
    setIdleSecondsRemaining(0);
    // Send a heartbeat to keep server session alive
    sendHeartbeat();
  }, [sendHeartbeat]);

  /**
   * Track user interactions for idle timeout (skipped for display/kiosk accounts)
   */
  useEffect(() => {
    if (!isAuthenticated || isDisplayAccount) return;

    const resetIdleTimer = () => {
      lastUserInteractionRef.current = Date.now();
      if (idleWarningVisible) {
        // If warning was visible, dismiss it on any interaction
        setIdleWarningVisible(false);
        setIdleSecondsRemaining(0);
      }
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, resetIdleTimer, { passive: true }));

    // Check idle status periodically
    idleCheckIntervalRef.current = setInterval(() => {
      if (!isAuthenticatedRef.current) return;

      const idleTime = Date.now() - lastUserInteractionRef.current;

      if (idleTime >= IDLE_TIMEOUT_MS) {
        // Session timed out — force logout
        clearInterval(idleCheckIntervalRef.current);
        setIdleWarningVisible(false);
        endSession();
        // Redirect to login
        window.location.href = '/login?reason=idle_timeout';
      } else if (idleTime >= IDLE_WARNING_MS && !idleWarningVisible) {
        setIdleWarningVisible(true);
        const remaining = Math.ceil((IDLE_TIMEOUT_MS - idleTime) / 1000);
        setIdleSecondsRemaining(remaining);
      } else if (idleWarningVisible) {
        const remaining = Math.ceil((IDLE_TIMEOUT_MS - idleTime) / 1000);
        setIdleSecondsRemaining(Math.max(0, remaining));
      }
    }, IDLE_CHECK_INTERVAL_MS);

    return () => {
      events.forEach(event => window.removeEventListener(event, resetIdleTimer));
      if (idleCheckIntervalRef.current) {
        clearInterval(idleCheckIntervalRef.current);
      }
    };
  }, [isAuthenticated, isDisplayAccount, idleWarningVisible, endSession]);

  /**
   * Handle page unload (closing tab, navigating away)
   * Try to end session gracefully
   */
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Use sendBeacon for reliable delivery on unload
      if (isAuthenticatedRef.current && navigator.sendBeacon) {
        navigator.sendBeacon('/api/activity/end-session', JSON.stringify({}));
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
  
  // ==================== CONTEXT VALUE ====================
  
  const contextValue = {
    isTracking,
    sessionId,
    activeTimeMs,
    endSession, // Expose for logout handling
    idleWarningVisible,
    dismissIdleWarning,
    idleSecondsRemaining
  };
  
  return (
    <ActivityTrackingContext.Provider value={contextValue}>
      {children}
    </ActivityTrackingContext.Provider>
  );
};

// ==================== HOOK ====================

/**
 * useActivityTracking hook
 * 
 * Access the current activity tracking state and controls.
 * 
 * @returns {Object} { isTracking, sessionId, activeTimeMs, endSession }
 */
export const useActivityTracking = () => {
  const context = useContext(ActivityTrackingContext);
  
  if (!context) {
    throw new Error('useActivityTracking must be used within an ActivityTrackingProvider');
  }
  
  return context;
};

// ==================== EXPORT ====================

export default ActivityTrackingContext;
